import { z } from 'zod';

/**
 * Zod schemas for the agent trace store. Used by the server's `agent-trace.service`
 * to validate rows before the service-role upsert ("save the right things" — a
 * malformed trace row is dropped, never written half-formed).
 *
 * Kept in sync with `packages/shared/src/types/agent-trace.ts`.
 */

export const ArmSchema = z.enum(['claude', 'native', 'openai']);
// OD2: which client surface originated the turn — orthogonal to arm (which
// engine ran). NOT the USER_MESSAGE `surface` routing enum ('web'|'native'|
// 'channel'), which gates runner placement and must never be widened.
// 'api' is a company's own server calling /v1 for one of its people (the platform API): the person, through
// their company's app.
export const SurfaceSchema = z.enum(['web', 'native-app', 'notch', 'extension', 'os-hands', 'cli', 'routine', 'probe', 'api']);
export const TerminalStatusSchema = z.enum(['running', 'ok', 'stopped', 'failed', 'paused', 'disconnected']);
export const ErrorSourceSchema = z.enum(['web', 'server', 'agent']);
export const ErrorSeveritySchema = z.enum(['warn', 'error', 'fatal']);

export const AgentTurnRecordSchema = z.object({
  traceId: z.string().min(1),
  sdkSessionId: z.string().optional(),
  taskId: z.string().min(1),
  userId: z.string().optional(),
  rootTaskId: z.string().optional(),
  spawnDepth: z.number().int().nonnegative().optional(),
  agentSlug: z.string().optional(),
  arm: ArmSchema.optional(),
  surface: SurfaceSchema.optional(),

  modelRequested: z.string().optional(),
  modelResponded: z.string().optional(),
  provider: z.string().optional(),

  inputTokens: z.number().nonnegative().optional(),
  outputTokens: z.number().nonnegative().optional(),
  cacheReadTokens: z.number().nonnegative().optional(),
  cacheCreationTokens: z.number().nonnegative().optional(),
  reasoningTokens: z.number().nonnegative().optional(),
  costUsd: z.number().nonnegative().optional(),
  /** Media-refs S0 — MAX per-call request size / image load across the turn. */
  requestBytes: z.number().nonnegative().optional(),
  imageCount: z.number().nonnegative().optional(),
  imageBytes: z.number().nonnegative().optional(),

  ttftMs: z.number().nonnegative().optional(),
  durationMs: z.number().nonnegative().optional(),

  finishReason: z.string().optional(),
  terminalReasonKind: z.string().optional(),
  terminalStatus: TerminalStatusSchema.optional(),
  errorClass: z.string().optional(),
  leakedErrorText: z.string().optional(),
  isSilentFailure: z.boolean().optional(),

  numToolCalls: z.number().int().nonnegative().optional(),
  numTurns: z.number().int().nonnegative().optional(),
  /** Orchestration walls — the turn's deepest helper + its summed refusals. */
  spawnMaxDepth: z.number().int().nonnegative().optional(),
  spawnRefused: z.number().int().nonnegative().optional(),
});

export const ToolCallRecordSchema = z.object({
  traceId: z.string().min(1),
  taskId: z.string().optional(),
  toolUseId: z.string().optional(),
  toolName: z.string().min(1),
  seq: z.number().int().nonnegative().optional(),
  // `null` — the call paused for a HITL interaction (error_class 'paused').
  success: z.boolean().nullable().optional(),
  errorClass: z.string().optional(),
  durationMs: z.number().nonnegative().optional(),
  calledNonexistentTool: z.boolean().optional(),
  malformedArgs: z.boolean().optional(),
  // Slice 2b: parent execute_code toolUseId for nested in-code calls (optional,
  // additive — pre-2b rows omit it).
  parentToolUseId: z.string().optional(),
  // Adoption metric (spec §7): ability slug for ability-namespaced Skill
  // invocations (optional, additive — non-ability rows omit it).
  abilitySlug: z.string().optional(),
});

export const ErrorEventRecordSchema = z.object({
  traceId: z.string().optional(),
  taskId: z.string().optional(),
  userId: z.string().optional(),
  category: z.string().min(1),
  errorClass: z.string().optional(),
  messageRedacted: z.string().optional(),
  terminalReasonKind: z.string().optional(),
  source: ErrorSourceSchema,
  severity: ErrorSeveritySchema.optional(),
  commitSha: z.string().optional(),
});
